import { httpsCallable } from 'firebase/functions'

import { functions } from '@/firebase'

interface GenerateStructuredOutputResponse {
  results: {
    correct_answer: string
  }
}

const generateStructuredOutput = httpsCallable<{ prompt: string; schema: any }, GenerateStructuredOutputResponse>(
  functions,
  'generateStructuredOutput',
)

export interface GenerateCorrectAnswerParams {
  question: string
  characterLimit: number
  language: string
}

const generateCorrectAnswer = async ({ question, characterLimit, language }: GenerateCorrectAnswerParams) => {
  const ai_prompt = `
    Generate a correct answer for the following question: "${question}"

    REQUIREMENTS:
    • The answer should ideally be within ${characterLimit.toString()} characters (the student character limit)
    • The answer must be written in ${language}
    • Provide a concise, accurate, and complete answer
    • Focus on the most essential information to answer the question
    • Use clear and simple language appropriate for ${language}
    • Ensure the answer directly addresses what is being asked
    • If the question has multiple acceptable answers, provide the most common or widely accepted one
    • For factual questions, ensure accuracy and relevance
    • For subjective questions, provide a well-reasoned response

    IMPORTANT:
    • Try to stay within ${characterLimit.toString()} characters, but prioritize completeness and accuracy
    • The answer MUST be written in ${language}
    • Be precise and avoid unnecessary words while maintaining clarity
    • If it's a fill-in-the-blank question, provide only the missing word(s)
    • For short answer questions, be comprehensive but concise
  `

  try {
    const { data } = await generateStructuredOutput({
      prompt: ai_prompt,
      schema: {
        type: 'object',
        properties: {
          correct_answer: {
            type: 'string',
            description: `The correct answer to the question, ideally limited to ${characterLimit.toString()} characters and written in ${language}`,
          },
        },
        required: ['correct_answer'],
      },
    })

    if (!data?.results) {
      throw new Error('No results returned from generateStructuredOutput')
    }

    const result = data.results

    return { success: true, ...result }
  } catch (error) {
    console.error('Error generating correct answer:', error)

    return {
      error: true,
      message: error instanceof Error ? error.message : 'Error generating correct answer',
    }
  }
}

export default generateCorrectAnswer
